import { useState, useEffect, CSSProperties, ReactNode } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import type { AuthUser } from 'aws-amplify/auth';
import Dashboard from './components/Dashboard';
import ClientOnboarding from './components/ClientOnboarding';
import ClientPortal from './components/ClientPortal';

// ─── Types ────────────────────────────────────────────────────────────────────

type Screen = 'signin' | 'signup' | 'confirm' | 'forgot' | 'forgot_confirm';

// ─── Shared sub-components ────────────────────────────────────────────────────

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#060a10',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, position: 'relative', overflow: 'hidden',
    }}>
      {/* Background blobs */}
      <div style={{
        position: 'absolute', top: '-160px', left: '-160px',
        width: 480, height: 480,
        background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-160px', right: '-160px',
        width: 480, height: 480,
        background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
        {/* Logo above card */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {/* F+bolt mark */}
          <div style={{
            width: 58, height: 58,
            background: 'linear-gradient(150deg, #0d2050 0%, #091428 100%)',
            borderRadius: 16,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 40px rgba(59,130,246,0.28)',
            marginBottom: 14,
          }}>
            <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
              <rect x="4" y="3" width="5" height="26" fill="white"/>
              <rect x="4" y="3" width="20" height="5" fill="white"/>
              <rect x="4" y="13" width="14" height="4" fill="white"/>
              <path d="M21 3 L25 3 L18 14 L23 14 L13 29 L10 29 L16 16 L11 16 Z" fill="#3b82f6"/>
            </svg>
          </div>
          {/* Wordmark */}
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.03em', color: '#e8edf5', lineHeight: 1.1 }}>
            intent<span style={{ color: '#3b82f6' }}>flo</span>
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: '#0b1220', border: '1px solid #162035',
          borderRadius: 16, padding: 32,
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({
  label, type = 'text', value, onChange, placeholder, autoFocus,
}: {
  label: string; type?: string; value: string;
  onChange: (v: string) => void; placeholder?: string; autoFocus?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const base: CSSProperties = {
    width: '100%', background: '#060a10', color: '#e8edf5',
    border: `1px solid ${focused ? '#3b82f6' : '#1a2840'}`,
    borderRadius: 8, padding: '10px 13px', fontSize: 13,
    fontFamily: 'Sora, sans-serif', outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: focused ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: '#64748b', letterSpacing: '0.03em' }}>{label}</label>
      <input
        type={type} value={value} placeholder={placeholder} autoFocus={autoFocus}
        style={base}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

function SubmitBtn({ children, loading, onClick }: {
  children: ReactNode; loading?: boolean; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={loading}
      onClick={onClick}
      style={{
        width: '100%', padding: '11px 0', borderRadius: 9, border: 'none',
        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
        color: '#fff', fontSize: 14, fontWeight: 600,
        fontFamily: 'Sora, sans-serif', cursor: loading ? 'not-allowed' : 'pointer',
        boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transition: 'opacity 0.15s', opacity: loading ? 0.7 : 1,
      }}
    >
      {loading && (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
          <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  );
}

function ErrBox({ msg }: { msg: string }) {
  return (
    <div style={{
      background: '#120608', border: '1px solid #7f1d1d',
      borderRadius: 8, padding: '10px 13px',
      color: '#fca5a5', fontSize: 13,
    }}>
      {msg}
    </div>
  );
}

function LinkBtn({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none', border: 'none', padding: 0,
        color: '#3b82f6', fontSize: 13, cursor: 'pointer',
        textDecoration: 'underline', textDecorationColor: 'rgba(59,130,246,0.4)',
      }}
    >
      {children}
    </button>
  );
}

// ─── Password strength checker ────────────────────────────────────────────────

function PwStrength({ pw, confirm }: { pw: string; confirm: string }) {
  const rules = [
    { label: '12+ characters',   ok: pw.length >= 12 },
    { label: 'Uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'Lowercase letter', ok: /[a-z]/.test(pw) },
    { label: 'Number',           ok: /\d/.test(pw) },
    { label: 'Symbol',           ok: /[^A-Za-z0-9]/.test(pw) },
    { label: 'Passwords match',  ok: pw.length > 0 && pw === confirm },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginTop: 4 }}>
      {rules.map(r => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: r.ok ? '#34d399' : '#3b4f6a' }}>
          <span style={{ fontSize: 10 }}>{r.ok ? '✓' : '○'}</span>
          {r.label}
        </div>
      ))}
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

export default function App() {
  // Public onboarding route — no auth required
  if (window.location.pathname.startsWith('/onboard')) {
    return <ClientOnboarding />;
  }

  // Landing page and public static pages — redirect to static files (bypasses SPA catch-all)
  const _p = window.location.pathname;
  if ((_p === '/' || _p.startsWith('/lp')) && !_p.startsWith('/login')) {
    window.location.replace('/lp/index.html');
    return null;
  }
  if (_p.startsWith('/pricing')) {
    const target = (_p.endsWith('.html') ? _p : _p.replace(/\/*$/, '/') + 'index.html') + window.location.search;
    window.location.replace(target);
    return null;
  }
  if (_p.startsWith('/privacy')) {
    window.location.replace('/privacy/index.html');
    return null;
  }
  if (_p.startsWith('/growthpartner')) {
    window.location.replace('/growthpartner/index.html');
    return null;
  }

  const [screen,   setScreen]   = useState<Screen>('signin');
  const [user,     setUser]     = useState<AuthUser | null>(null);
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [email,    setEmail]    = useState('');
  const [pw,       setPw]       = useState('');
  const [pwConf,   setPwConf]   = useState('');
  const [first,    setFirst]    = useState('');
  const [last,     setLast]     = useState('');
  const [code,     setCode]     = useState('');
  const [newPw,    setNewPw]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState('');
  const [checking, setChecking] = useState(true);

  function go(s: Screen) { setErr(''); setCode(''); setScreen(s); }

  // Check if already signed in
  useEffect(() => {
    getCurrentUser()
      .then(async (u) => {
        setUser(u);
        try {
          const session = await fetchAuthSession();
          const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ?? [];
          setIsAdmin(groups.includes('Admins'));
        } catch {
          setIsAdmin(false);
        }
      })
      .catch(async () => {
        // No active session — clear any stale Amplify state
        try { await amplifySignOut({ global: false }); } catch {}
      })
      .finally(() => setChecking(false));
  }, []);

  async function handleSignOut() {
    await amplifySignOut();
    setUser(null);
    setIsAdmin(false);
    go('signin');
  }

  // ── Sign In ──────────────────────────────────────────────────────────────
  async function doSignIn() {
    setLoading(true); setErr('');
    try {
      await signIn({ username: email, password: pw });
      const u = await getCurrentUser();
      setUser(u);
      const session = await fetchAuthSession();
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ?? [];
      setIsAdmin(groups.includes('Admins'));
    } catch (e: any) {
      // Already signed in — just load the existing session
      if (e.name === 'UserAlreadyAuthenticatedException' || e.message?.includes('already a signed in user')) {
        try {
          const u = await getCurrentUser();
          setUser(u);
          const session = await fetchAuthSession();
          const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) ?? [];
          setIsAdmin(groups.includes('Admins'));
        } catch (inner: any) {
          setErr(inner.message ?? 'Sign in failed');
        }
      } else {
        setErr(e.message ?? 'Sign in failed');
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Sign Up ──────────────────────────────────────────────────────────────
  async function doSignUp() {
    if (pw !== pwConf) { setErr('Passwords do not match'); return; }
    setLoading(true); setErr('');
    try {
      await signUp({
        username: email, password: pw,
        options: { userAttributes: { given_name: first, family_name: last, email } },
      });
      go('confirm');
    } catch (e: any) {
      setErr(e.message ?? 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  // ── Confirm ──────────────────────────────────────────────────────────────
  async function doConfirm() {
    setLoading(true); setErr('');
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      go('signin');
    } catch (e: any) {
      setErr(e.message ?? 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  // ── Forgot / Reset ───────────────────────────────────────────────────────
  async function doForgot() {
    setLoading(true); setErr('');
    try {
      await resetPassword({ username: email });
      go('forgot_confirm');
    } catch (e: any) {
      setErr(e.message ?? 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  }

  async function doReset() {
    setLoading(true); setErr('');
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword: newPw });
      go('signin');
    } catch (e: any) {
      setErr(e.message ?? 'Password reset failed');
    } finally {
      setLoading(false);
    }
  }

  // ── Authenticated ────────────────────────────────────────────────────────
  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: '#060a10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="32" height="32" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
          <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
          <path d="M8 2a6 6 0 0 1 6 6" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
    );
  }

  if (user) {
    if (isAdmin) return <Dashboard user={user} onSignOut={handleSignOut} />;
    return <ClientPortal onSignOut={handleSignOut} />;
  }

  const heading: CSSProperties = { fontSize: 17, fontWeight: 700, color: '#e8edf5', marginBottom: 4 };
  const sub: CSSProperties     = { fontSize: 13, color: '#3b4f6a', marginBottom: 24 };
  const stack: CSSProperties   = { display: 'flex', flexDirection: 'column', gap: 14 };
  const center: CSSProperties  = { textAlign: 'center', fontSize: 13, color: '#3b4f6a', marginTop: 8 };

  // ── Sign In screen ───────────────────────────────────────────────────────
  if (screen === 'signin') return (
    <AuthCard>
      <div style={heading}>Sign in</div>
      <div style={sub}>Welcome back to intentFlo</div>
      <div style={stack}>
        {err && <ErrBox msg={err} />}
        <Field label="Work email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" autoFocus />
        <Field label="Password"   type="password" value={pw} onChange={setPw} />
        <div style={{ textAlign: 'right', marginTop: -6 }}>
          <LinkBtn onClick={() => go('forgot')}>Forgot password?</LinkBtn>
        </div>
        <SubmitBtn loading={loading} onClick={doSignIn}>Sign In</SubmitBtn>
        <div style={center}>
          No account? <LinkBtn onClick={() => go('signup')}>Create one</LinkBtn>
        </div>
      </div>
    </AuthCard>
  );

  // ── Sign Up screen ───────────────────────────────────────────────────────
  if (screen === 'signup') return (
    <AuthCard>
      <div style={heading}>Create account</div>
      <div style={sub}>Get started with intentFlo</div>
      <div style={stack}>
        {err && <ErrBox msg={err} />}
        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.22)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#fbbf24', lineHeight: 1.6 }}>
          ⚠️ Use the <strong>same email address</strong> your account was set up with. Using a different email means your portal data won't load.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="First name" value={first} onChange={setFirst} autoFocus />
          <Field label="Last name"  value={last}  onChange={setLast} />
        </div>
        <Field label="Work email"        type="email"    value={email}  onChange={setEmail}  placeholder="you@company.com" />
        <Field label="Password"          type="password" value={pw}     onChange={setPw} />
        <Field label="Confirm password"  type="password" value={pwConf} onChange={setPwConf} />
        <PwStrength pw={pw} confirm={pwConf} />
        <SubmitBtn loading={loading} onClick={doSignUp}>Create Account</SubmitBtn>
        <div style={center}>
          Already have an account? <LinkBtn onClick={() => go('signin')}>Sign in</LinkBtn>
        </div>
      </div>
    </AuthCard>
  );

  // ── Confirm screen ───────────────────────────────────────────────────────
  if (screen === 'confirm') return (
    <AuthCard>
      <div style={heading}>Verify your email</div>
      <div style={sub}>We sent a code to <strong style={{ color: '#60a5fa' }}>{email}</strong></div>
      <div style={stack}>
        {err && <ErrBox msg={err} />}
        <Field label="Verification code" value={code} onChange={setCode} placeholder="123456" autoFocus />
        <SubmitBtn loading={loading} onClick={doConfirm}>Verify</SubmitBtn>
        <div style={center}>
          <LinkBtn onClick={() => go('signin')}>← Back to sign in</LinkBtn>
        </div>
      </div>
    </AuthCard>
  );

  // ── Forgot password screen ───────────────────────────────────────────────
  if (screen === 'forgot') return (
    <AuthCard>
      <div style={heading}>Reset password</div>
      <div style={sub}>Enter your email and we'll send a reset code</div>
      <div style={stack}>
        {err && <ErrBox msg={err} />}
        <Field label="Work email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" autoFocus />
        <SubmitBtn loading={loading} onClick={doForgot}>Send Reset Code</SubmitBtn>
        <div style={center}>
          <LinkBtn onClick={() => go('signin')}>← Back to sign in</LinkBtn>
        </div>
      </div>
    </AuthCard>
  );

  // ── Reset confirm screen ─────────────────────────────────────────────────
  return (
    <AuthCard>
      <div style={heading}>Enter new password</div>
      <div style={sub}>Check your email for the reset code</div>
      <div style={stack}>
        {err && <ErrBox msg={err} />}
        <Field label="Reset code"     value={code}  onChange={setCode}  placeholder="123456" autoFocus />
        <Field label="New password"   type="password" value={newPw} onChange={setNewPw} />
        <SubmitBtn loading={loading} onClick={doReset}>Reset Password</SubmitBtn>
        <div style={center}>
          <LinkBtn onClick={() => go('signin')}>← Back to sign in</LinkBtn>
        </div>
      </div>
    </AuthCard>
  );
}

